// Vue « Rendez-vous » : mes plages ouvertes à la réservation, l’adresse publique, les rendez-vous pris.
import { DURATIONS, type Booking, type BookingWindow } from '../core/booking';
import { buildIcs } from '../core/ics';
import { hm, parseTime, prettyDate, time } from '../core/time';
import { google } from '../data/google';
import type { Store } from '../data/store';
import type { MountedView } from './contacts';
import { download, el, esc, on } from './dom';
import { icon } from './icons';
import { syncBanner } from './shell';
import { showToast } from './toast';

const DAY_LABELS = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

interface ServerConfig { turnstileSiteKey: string | null; bookingEnabled: boolean }

/**
 * Un tap sur « Accepter » depuis la fenêtre de décision : accepte, puis montre le lien Meet dans la même
 * fenêtre plutôt que de la refermer, pour qu’il soit là sous les yeux à copier ou vérifier.
 */
function openBookingDecision(store: Store, b: Booking): void {
  const d = document.createElement('dialog');
  d.setAttribute('aria-labelledby', 'rdvDecideTitle');
  const me = store.state.profile;
  const when = `${prettyDate(b.startAt, me.tz, true)} · ${time(me.tz, b.startAt)} – ${time(me.tz, b.endAt)}`;

  function paint(inner: string): void {
    d.innerHTML = `<form method="dialog" novalidate>
      <header class="dlg-head"><div><h2 id="rdvDecideTitle">Demande de rendez-vous</h2><p>${esc(when)}</p></div>
        <button class="icon-btn" type="button" data-close aria-label="Fermer">${icon('close')}</button></header>
      <div class="dlg-body">${inner}</div>
      <footer class="dlg-foot" data-foot></footer></form>`;
    d.querySelectorAll('[data-close]').forEach((btn) => { (btn as HTMLElement).onclick = () => d.close(); });
  }

  function paintAsk(): void {
    paint(`
      <p>${esc(b.name)} · <a href="mailto:${esc(b.email)}">${esc(b.email)}</a></p>
      ${b.note ? `<p class="hint">« ${esc(b.note)} »</p>` : ''}
      <p class="hint">Le créneau est réservé pour ce visiteur tant que vous n’avez pas décidé.</p>`);
    el(d, '[data-foot]').innerHTML = `<button class="btn ghost danger" type="button" data-decide-decline>Refuser</button><button class="btn primary" type="button" data-decide-approve>${icon('check')}Accepter</button>`;
    el<HTMLButtonElement>(d, '[data-decide-approve]').onclick = async (e) => {
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      try {
        const r = await store.respondBooking(b.id, 'approve');
        paint(`<p>${icon('check')} Rendez-vous confirmé avec ${esc(b.name)}.</p>`
          + (r.meetUrl
            ? `<div class="field"><label for="rdvDecideMeet">Visio</label><div class="linkrow"><input type="text" id="rdvDecideMeet" readonly spellcheck="false" value="${esc(r.meetUrl)}"><button class="btn primary" type="button" data-decide-copy>${icon('copy')}Copier</button></div></div><p class="hint">Déjà ajouté à votre agenda Google, avec ${esc(b.name)}${b.guests.length ? ` et ${b.guests.length} invité${b.guests.length > 1 ? 's' : ''}` : ''}${r.invited ? ', prévenus par e-mail' : ''}.</p>`
            : '<p class="hint">Sans Google Agenda relié, prévenez la personne vous-même.</p>'));
        el(d, '[data-foot]').innerHTML = `<button class="btn primary" type="button" data-close>Fermer</button>`;
        d.querySelectorAll('[data-close]').forEach((btn2) => { (btn2 as HTMLElement).onclick = () => d.close(); });
        const copyBtn = d.querySelector<HTMLButtonElement>('[data-decide-copy]');
        if (copyBtn && r.meetUrl) copyBtn.onclick = () => { void navigator.clipboard?.writeText(r.meetUrl!).then(() => showToast({ id: 0, kind: 'info', text: 'Lien copié.' })); };
      } catch (err) {
        showToast({ id: 0, kind: 'error', text: err instanceof Error ? err.message : 'Impossible de confirmer.' });
        btn.disabled = false;
      }
    };
    el<HTMLButtonElement>(d, '[data-decide-decline]').onclick = async (e) => {
      if (!confirm(`Refuser la demande de ${b.name} ? Si votre agenda Google est relié, un e-mail poli le lui dira.`)) return;
      const btn = e.currentTarget as HTMLButtonElement;
      btn.disabled = true;
      try {
        await store.respondBooking(b.id, 'decline');
        paint(`<p>Demande refusée. Le créneau est de nouveau libre.</p>`);
        el(d, '[data-foot]').innerHTML = `<button class="btn primary" type="button" data-close>Fermer</button>`;
        d.querySelectorAll('[data-close]').forEach((btn2) => { (btn2 as HTMLElement).onclick = () => d.close(); });
      } catch (err) {
        showToast({ id: 0, kind: 'error', text: err instanceof Error ? err.message : 'Impossible de refuser.' });
        btn.disabled = false;
      }
    };
  }

  paintAsk();
  d.addEventListener('close', () => d.remove());
  document.body.append(d);
  d.showModal();
}

export function mountRdv(main: HTMLElement, store: Store, focusBookingId?: string): MountedView {
  let windows: BookingWindow[] = store.state.booking.windows.map((w) => ({ ...w }));
  let serverConfig: ServerConfig | null | 'error' = null;
  let focusOpened = false;
  const publicUrl = `${location.origin}/rdv`;

  main.innerHTML = `
    <div data-banner></div>
    <section class="page-head"><div><div class="eyebrow">Prise de rendez-vous</div><h1>Rendez-vous</h1><p>Vos clients et prospects choisissent un créneau parmi vos plages, sans échange de mails.</p></div></section>
    <div class="stack">
      <section class="card">
        <h2>Ma page publique</h2>
        <p class="sub">Un bouton « Prendre rendez-vous » sur acte-5.com peut pointer vers cette adresse.</p>
        <div class="linkrow"><input type="text" readonly value="${esc(publicUrl)}" data-public-url spellcheck="false"><button class="btn" type="button" data-copy-url>${icon('copy')}Copier</button><a class="btn" href="${esc(publicUrl)}" target="_blank" rel="noopener">${icon('globe')}Voir</a></div>
        <div data-server-state class="hint"></div>
        <label class="check-label"><input type="checkbox" data-enabled ${store.state.booking.enabled ? 'checked' : ''}> Réservation ouverte aux visiteurs</label>
        <label class="check-label"><input type="checkbox" data-approval ${store.state.booking.requireApproval ? 'checked' : ''}> Valider chaque demande moi-même avant qu’elle soit confirmée</label>
        <p class="hint">Sans cette case, un créneau réservé est confirmé tout de suite. Avec, le visiteur envoie une demande, le créneau reste bloqué le temps de votre décision, et vous choisissez d’accepter ou de refuser dans « Rendez-vous à venir ».</p>
      </section>
      <form class="card" novalidate data-form>
        <h2>Mes plages</h2>
        <p class="sub">À l’heure de ${esc(store.state.profile.place)}. Les rendez-vous ne pourront être pris que dans ces plages, hors créneaux déjà réservés.</p>
        <div data-windows class="windows"></div>
        <div class="two">
          <div class="field"><label for="r-duration">Durée d’un rendez-vous</label><select id="r-duration">${DURATIONS.map((d) => `<option value="${d}">${d} min</option>`).join('')}</select></div>
          <div class="field"><label for="r-buffer">Marge entre deux rendez-vous</label><select id="r-buffer">${[0, 10, 15, 30, 45, 60].map((d) => `<option value="${d}">${d} min</option>`).join('')}</select></div>
          <div class="field"><label for="r-notice">Délai minimum avant un rendez-vous</label><select id="r-notice">${[2, 4, 12, 24, 48, 72, 168].map((h) => `<option value="${h}">${h < 24 ? h + ' h' : h / 24 + ' jour' + (h > 24 ? 's' : '')}</option>`).join('')}</select></div>
          <div class="field"><label for="r-horizon">Réservable jusqu’à</label><select id="r-horizon">${[7, 14, 30, 60, 90].map((d) => `<option value="${d}">${d} jours</option>`).join('')}</select></div>
        </div>
        <h2 style="margin-top:6px">Texte de la page</h2>
        <div class="field"><label for="r-title">Titre du rendez-vous</label><input id="r-title" maxlength="80" placeholder="Visio · Acte 5"></div>
        <div class="two">
          <div class="field"><label for="r-intro-fr">Texte d’accueil, en français</label><textarea id="r-intro-fr" maxlength="600" rows="3" placeholder="Laissez vide pour le texte proposé : un premier échange en visio…"></textarea></div>
          <div class="field"><label for="r-intro-en">Texte d’accueil, en anglais</label><textarea id="r-intro-en" maxlength="600" rows="3" placeholder="Leave empty for the suggested text: a first video call…"></textarea></div>
        </div>
        <p class="error" role="alert" data-error></p>
        <div class="card-actions"><button class="btn primary" type="submit" data-save>Enregistrer</button></div>
      </form>
      <section class="card">
        <h2>Rendez-vous à venir</h2>
        <div data-bookings></div>
      </section>
    </div>`;

  const form = el<HTMLFormElement>(main, '[data-form]');
  const durationEl = el<HTMLSelectElement>(main, '#r-duration');
  const bufferEl = el<HTMLSelectElement>(main, '#r-buffer');
  const noticeEl = el<HTMLSelectElement>(main, '#r-notice');
  const horizonEl = el<HTMLSelectElement>(main, '#r-horizon');
  const titleEl = el<HTMLInputElement>(main, '#r-title');
  const introFrEl = el<HTMLTextAreaElement>(main, '#r-intro-fr');
  const introEnEl = el<HTMLTextAreaElement>(main, '#r-intro-en');

  function fillForm(): void {
    const b = store.state.booking;
    durationEl.value = String(b.duration);
    bufferEl.value = String(b.buffer);
    noticeEl.value = String(b.noticeHours);
    horizonEl.value = String(b.horizonDays);
    titleEl.value = b.title;
    introFrEl.value = b.introFr;
    introEnEl.value = b.introEn;
    windows = b.windows.map((w) => ({ ...w }));
    paintWindows();
  }

  function paintWindows(): void {
    el(main, '[data-windows]').innerHTML = DAY_ORDER.map((d) => {
      const rows = windows.map((w, i) => ({ w, i })).filter((x) => x.w.day === d);
      return `<div class="wday"><div class="wday-name">${DAY_LABELS[d]}</div><div class="wday-ranges">${rows.length ? rows.map(({ w, i }) => `<span class="wrange"><input type="time" step="900" value="${esc(hm(w.start))}" data-w-start="${i}" aria-label="Début"> – <input type="time" step="900" value="${esc(hm(w.end))}" data-w-end="${i}" aria-label="Fin"><button class="icon-btn" type="button" data-w-del="${i}" aria-label="Retirer cette plage">${icon('close')}</button></span>`).join('') : '<span class="hint">Fermé</span>'}<button class="btn small ghost" type="button" data-w-add="${d}">${icon('plus')}Ajouter une plage</button></div></div>`;
    }).join('');
  }

  async function loadServerState(): Promise<void> {
    try {
      const res = await fetch('/api/rdv/config', { cache: 'no-store' });
      serverConfig = res.ok ? ((await res.json()) as ServerConfig) : 'error';
    } catch { serverConfig = 'error'; }
    paintServerState();
  }

  function paintServerState(): void {
    const box = el(main, '[data-server-state]');
    if (serverConfig === null) { box.textContent = 'Vérification du serveur…'; return; }
    if (serverConfig === 'error') { box.innerHTML = `${icon('alert')} Serveur de réservation injoignable (les fonctions serveur ne tournent pas ici).`; return; }
    box.innerHTML = serverConfig.bookingEnabled
      ? `${icon('check')} Serveur prêt à enregistrer les réservations${serverConfig.turnstileSiteKey ? ' · protection anti-robots active' : ' · <b>protection anti-robots à configurer</b> (voir la notice RDV)'}.`
      : `${icon('alert')} <b>Le serveur ne peut pas encore enregistrer de réservation</b> : la clé de service Supabase n’est pas posée dans Cloudflare (notice docs/RDV.md, étape 2).`;
  }

  on(main, 'click', '[data-copy-url]', () => { void navigator.clipboard?.writeText(publicUrl).then(() => showToast({ id: 0, kind: 'info', text: 'Adresse copiée.' })).catch(() => { /* rien */ }); });
  on(main, 'change', '[data-enabled]', (_e, t) => {
    const on_ = (t as HTMLInputElement).checked;
    if (on_ && !windows.length) { (t as HTMLInputElement).checked = false; showToast({ id: 0, kind: 'error', text: 'Ajoutez au moins une plage avant d’ouvrir la réservation.' }); return; }
    void store.saveBookingSettings({ enabled: on_, windows }).then(() => showToast({ id: 0, kind: 'info', text: on_ ? 'Réservation ouverte.' : 'Réservation fermée.' })).catch(() => { (t as HTMLInputElement).checked = !on_; });
  });
  on(main, 'change', '[data-approval]', (_e, t) => {
    const on_ = (t as HTMLInputElement).checked;
    void store.saveBookingSettings({ requireApproval: on_, windows }).then(() => showToast({ id: 0, kind: 'info', text: on_ ? 'Chaque demande attendra votre validation.' : 'Les rendez-vous sont de nouveau confirmés tout de suite.' })).catch((e) => { (t as HTMLInputElement).checked = !on_; showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Enregistrement impossible.' }); });
  });
  on(main, 'click', '[data-w-add]', (_e, t) => { windows.push({ day: Number(t.dataset.wAdd), start: 540, end: 720 }); paintWindows(); });
  on(main, 'click', '[data-w-del]', (_e, t) => { windows.splice(Number(t.dataset.wDel), 1); paintWindows(); });
  main.addEventListener('change', (e) => {
    const t = e.target as HTMLInputElement;
    const s = t.dataset.wStart, en = t.dataset.wEnd;
    if (s !== undefined) { const v = parseTime(t.value); if (!Number.isNaN(v)) windows[Number(s)]!.start = v; }
    if (en !== undefined) { const v = parseTime(t.value); if (!Number.isNaN(v)) windows[Number(en)]!.end = v; }
  });
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errorEl = el(main, '[data-error]');
    errorEl.textContent = '';
    const bad = windows.find((w) => w.end <= w.start);
    if (bad) { errorEl.textContent = `Une plage du ${DAY_LABELS[bad.day]!.toLowerCase()} finit avant de commencer.`; return; }
    const btn = el<HTMLButtonElement>(main, '[data-save]');
    btn.disabled = true;
    try {
      await store.saveBookingSettings({
        windows, duration: Number(durationEl.value), buffer: Number(bufferEl.value), noticeHours: Number(noticeEl.value), horizonDays: Number(horizonEl.value),
        title: titleEl.value, introFr: introFrEl.value, introEn: introEnEl.value,
      });
      showToast({ id: 0, kind: 'info', text: 'Réglages de rendez-vous enregistrés.' });
    } catch (err) { errorEl.textContent = err instanceof Error ? err.message : 'Enregistrement impossible.'; }
    finally { btn.disabled = false; }
  });
  on(main, 'click', '[data-cancel]', (_e, t) => {
    const b = store.state.bookings.find((x) => x.id === t.dataset.cancel);
    if (!b) return;
    if (!confirm(`Annuler le rendez-vous avec ${b.name} ? ${b.googleEventId ? `${b.guests.length ? 'Les invités seront prévenus' : 'L’invité sera prévenu'} par Google Agenda et l’événement sera retiré de votre agenda.` : 'Prévenez la personne vous-même, aucun e-mail n’est envoyé. Ce rendez-vous n’a pas été mis dans votre agenda par l’application : s’il y est, retirez-le vous-même.'}`)) return;
    void store.cancelBooking(b.id).then(async () => {
      showToast({ id: 0, kind: 'info', text: 'Rendez-vous annulé.' });
      if (!b.googleEventId) return;
      try {
        const r = await google.cancelBooking(b.id);
        // Dire quand l’événement est resté : sans cela, il dort dans l’agenda sans que rien ne le signale.
        if (!r.deleted) showToast({ id: 0, kind: 'error', text: 'Annulé ici, mais l’événement est resté dans votre agenda Google : reliez à nouveau Google Agenda dans Préférences, puis retirez-le à la main.' });
      } catch (e) { showToast({ id: 0, kind: 'error', text: 'Rendez-vous annulé ici, mais pas dans Google Agenda : ' + (e instanceof Error ? e.message : '') }); }
    }).catch(() => { /* déjà signalé */ });
  });
  on(main, 'click', '[data-approve]', (_e, t) => {
    const btn = t as HTMLButtonElement;
    const b = store.state.bookings.find((x) => x.id === btn.dataset.approve);
    if (!b) return;
    btn.disabled = true;
    void store.respondBooking(b.id, 'approve')
      .then(() => showToast({ id: 0, kind: 'info', text: `Rendez-vous confirmé avec ${b.name}.` }))
      .catch((e) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Impossible de confirmer.' }))
      .finally(() => { btn.disabled = false; });
  });
  on(main, 'click', '[data-decline]', (_e, t) => {
    const b = store.state.bookings.find((x) => x.id === t.dataset.decline);
    if (!b) return;
    if (!confirm(`Refuser la demande de ${b.name} ? Si votre agenda Google est relié, un e-mail poli le lui dira.`)) return;
    const btn = t as HTMLButtonElement;
    btn.disabled = true;
    void store.respondBooking(b.id, 'decline')
      .then(() => showToast({ id: 0, kind: 'info', text: 'Demande refusée.' }))
      .catch((e) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Impossible de refuser.' }))
      .finally(() => { btn.disabled = false; });
  });
  on(main, 'click', '[data-ics]', (_e, t) => {
    const b = store.state.bookings.find((x) => x.id === t.dataset.ics);
    if (!b) return;
    const me = store.state.profile;
    const ics = buildIcs({ uid: `a5w-rdv-${b.id}@acte-5.com`, start: b.startAt, end: b.endAt, summary: `${store.state.booking.title} · ${b.name}`, description: `${b.name} <${b.email}>${b.guests.length ? '\n+ ' + b.guests.join(', ') : ''}${b.note ? '\n' + b.note : ''}`, now: Date.now() });
    download(`rendez-vous-${time(me.tz, b.startAt).replace(':', 'h')}-${prettyDate(b.startAt, me.tz).replace(/[^a-z0-9]+/gi, '-')}.ics`, ics, 'text/calendar;charset=utf-8');
  });

  function bookingRow(b: Booking): string {
    const me = store.state.profile;
    const sameTz = b.tz === me.tz;
    const pending = b.status === 'pending';
    const detail = `${pending ? '<span class="role-tag supplier">En attente de votre décision</span> ' : b.googleEventId ? '<span class="role-tag">Invitation Google envoyée</span> ' : '<span class="muted">sans invitation</span> · '}${esc(b.name)} · <a href="mailto:${esc(b.email)}">${esc(b.email)}</a>${b.guests.length ? `<br>+ ${b.guests.map((g) => `<a href="mailto:${esc(g)}">${esc(g)}</a>`).join(', ')}` : ''}${sameTz ? '' : ` · chez lui ${esc(time(b.tz, b.startAt))} (${esc(b.tz.replace(/_/g, ' '))})`}${b.note ? `<br>« ${esc(b.note)} »` : ''}${b.meetUrl ? `<br><a href="${esc(b.meetUrl)}" target="_blank" rel="noopener">${esc(b.meetUrl)}</a>` : ''}`;
    const actions = pending
      ? `<button class="btn small primary" type="button" data-approve="${esc(b.id)}">${icon('check')}Accepter</button><button class="btn small ghost danger" type="button" data-decline="${esc(b.id)}">Refuser</button>`
      : `<button class="btn small" type="button" data-ics="${esc(b.id)}" title="Ajouter à mon agenda">${icon('download')}</button><button class="btn small ghost danger" type="button" data-cancel="${esc(b.id)}">Annuler</button>`;
    return `<div class="trash-row${pending ? ' pending' : ''}"><div><b>${esc(prettyDate(b.startAt, me.tz, true))} · ${esc(time(me.tz, b.startAt))} – ${esc(time(me.tz, b.endAt))}</b><small>${detail}</small></div>
      <div class="card-actions">${actions}</div></div>`;
  }

  function update(): void {
    el(main, '[data-banner]').innerHTML = syncBanner(store);
    el<HTMLInputElement>(main, '[data-enabled]').checked = store.state.booking.enabled;
    if (document.activeElement === null || !form.contains(document.activeElement)) fillForm();
    // Ouverte par une notification (?booking=<id>) : sa décision s’ouvre toute seule, une fois la donnée là.
    if (focusBookingId && !focusOpened) {
      const b = store.state.bookings.find((x) => x.id === focusBookingId);
      if (b && b.status === 'pending') { focusOpened = true; openBookingDecision(store, b); }
      else if (b) focusOpened = true; // déjà décidée entre-temps (depuis un autre appareil, par exemple)
    }
    const list = store.upcomingBookings;
    const past = store.state.bookings.filter((b) => b.status === 'confirmed' && b.endAt <= Date.now()).length;
    el(main, '[data-bookings]').innerHTML = list.length
      ? list.map(bookingRow).join('') + (list.some((b) => b.status === 'confirmed' && !b.googleEventId) ? `<p class="hint" style="margin-top:8px">Sans Google Agenda relié au moment de la réservation, aucune invitation n’est envoyée : ajoutez le rendez-vous à votre agenda avec le bouton, et envoyez le lien de visio vous-même.</p>` : '')
      : `<p class="hint">Aucun rendez-vous à venir.${past ? ` ${past} rendez-vous passé${past > 1 ? 's' : ''} récemment.` : ''}</p>`;
  }

  fillForm();
  update();
  void loadServerState();
  return { update };
}
